import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  FormHelperText,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { CustomField } from "./CandidateDetailsConfiguration";

interface CustomFieldRowBaseProps {
  field: CustomField;
  error?: { name?: string; type?: string; options?: string };
  onChange: (updatedField: CustomField) => void;
  onSave: () => void;
  onDelete: () => void;
  isNew: boolean;
}

const fieldTypeOptions = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Dropdown", value: "dropdown" },
  { label: "Boolean", value: "boolean" },
];

const CustomFieldRowBase: React.FC<CustomFieldRowBaseProps> = ({
  field,
  onChange,
  onSave,
  onDelete,
  error,
  isNew,
}) => {
  const theme = useTheme();

  const handleDropdownOptionsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const optionsRaw = e.target.value;
    const options = optionsRaw
      .split(",")
      .map((opt) => opt.trim())
      .filter((opt) => opt);
    onChange({ ...field, optionsRaw, options });
  };

  // dynamic grid template based on field.type
  const gridTemplate = field.type === "dropdown"
    ? "1.5fr 1.2fr 1.5fr 0.8fr 0.8fr"
    : "1.5fr 1.2fr 0.8fr 0.8fr";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        mt: 2,
        boxShadow: "none",
        borderRadius: "12px",
        borderColor: "#F4EBFF",
        overflow: "hidden",
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          px: 2,
          pt: 1.2,
          pb: 0.8,
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        <Typography variant="caption">Field Name</Typography>
        <Typography variant="caption">Field Type</Typography>
        {field.type === "dropdown" && (
          <Typography variant="caption">Options</Typography>
        )}
        <Typography variant="caption" sx={{ textAlign: "center" }}>
          Mandatory Field
        </Typography>
        <Typography variant="caption" sx={{ textAlign: "center" }}>
          Action
        </Typography>
      </Box>

      {/* Input Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          alignItems: "center",
          px: 2,
          py: 1.5,
          gap: 2,
          bgcolor: theme.palette.primary.light,
          borderRadius: "12px",
        }}
      >
        {/* Field Name */}
        <Box>
          <TextField
            fullWidth
            size="small"
            value={field.name}
            onChange={(e) => onChange({ ...field, name: e.target.value })}
            placeholder="Enter field name"
            error={!!error?.name}
            autoFocus={isNew}
            sx={{ background: "#fff", borderRadius: "8px" }}
          />
          {error?.name && <FormHelperText error>{error.name}</FormHelperText>}
        </Box>

        {/* Field Type */}
        <FormControl
          fullWidth
          size="small"
          sx={{ background: "#fff", borderRadius: "8px" }}
          error={!!error?.type}
        >
          <Select
            value={field.type}
            onChange={(e) => onChange({ ...field, type: e.target.value })}
            displayEmpty
          >
            {fieldTypeOptions.map((ft) => (
              <MenuItem key={ft.value} value={ft.value}>
                {ft.label}
              </MenuItem>
            ))}
          </Select>
          {error?.type && <FormHelperText>{error.type}</FormHelperText>}
        </FormControl>

        {/* Dropdown Options (only visible if dropdown) */}
        {field.type === "dropdown" && (
          <Box>
            <TextField
              fullWidth
              size="small"
              value={field.optionsRaw || ""}
              onChange={handleDropdownOptionsChange}
              placeholder="Enter options, comma separated"
              sx={{ background: "#fff", borderRadius: "8px" }}
              error={!!error?.options}
            />
            {error?.options && (
              <FormHelperText error>{error.options}</FormHelperText>
            )}
          </Box>
        )}

        {/* Mandatory Checkbox */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Checkbox
            checked={field.mandatory}
            onChange={(e) => onChange({ ...field, mandatory: e.target.checked })}
            sx={{
              color: theme.palette.secondary.main,
              "&.Mui-checked": { color: theme.palette.primary.main },
              p: 0.5,
            }}
          />
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            p: 0.5,
          }}
        >
          <IconButton
            onClick={onSave}
            color="primary"
            title="Save Custom Field"
            size="small"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onDelete}
            color="primary"
            title="Delete Custom Field"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default CustomFieldRowBase;
